"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SubProductsGrid() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const languages = [{
    id: 1,
    flag: "ST",
    flagLabel: "Estructurales",
    greeting: "Resistencia.",
    language: "Acero Estructural",
    tagline: "Vigas, canales y ángulos para cimientos sólidos.",
    learners: "1,240",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/industrial_steel_beams.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/industrial_steel_beams.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/industrial_steel_beams.png-wvc-sizes",
    alt: "Vigas de acero estructural apiladas en almacén industrial"
  }, {
    id: 2,
    flag: "PL",
    flagLabel: "Planos",
    greeting: "Precisión.",
    language: "Productos Planos",
    tagline: "Láminas y placas con acabados de alta calidad.",
    learners: "850",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/steel_sheets_warehouse.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/steel_sheets_warehouse.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/steel_sheets_warehouse.png-wvc-sizes",
    alt: "Rollos de lámina de acero en planta de procesamiento"
  }, {
    id: 3,
    flag: "TB",
    flagLabel: "Tubería",
    greeting: "Versatilidad.",
    language: "Tubería Industrial",
    tagline: "Soluciones tubulares para conducción y estructura.",
    learners: "920",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/industrial_steel_pipes.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/industrial_steel_pipes.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/industrial_steel_pipes.webp-wvc-sizes",
    alt: "Tubería de acero industrial de diversos diámetros"
  }];
  return <section data-section-id="640" id="sub-products-grid" className="bg-background text-foreground py-24 md:py-32 lg:py-[140px]">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={introRef} className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16 transition-all duration-700 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7 max-w-[720px]">
            <span className="font-mono text-xs font-medium uppercase tracking-[0.2em] text-primary">
              Nuestras Divisiones
            </span>
            <h2 className="font-default mt-5 text-4xl font-bold leading-[1.1] text-foreground md:text-5xl lg:text-6xl">
              Calidad certificada.{" "}
              <em className="italic font-normal text-secondary">Acero Kalisch</em>{" "}
              para proyectos de alto impacto.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="font-default text-base leading-relaxed text-muted-foreground md:text-lg">
              Contamos con un catálogo extenso diseñado para satisfacer las demandas 
              más exigentes de la industria de la construcción y manufactura.
            </p>
          </div>
        </div>

        {}
        <div ref={cardsRef} className="mt-16 grid grid-cols-1 gap-8 md:gap-6 lg:grid-cols-3 lg:gap-8 md:mt-20">
          {languages.map((lang, i) => <Link key={lang.id} to="/ProductsPage" data-index={i} className={`group relative flex flex-col overflow-hidden rounded-lg bg-card shadow-md transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-xl md:h-[480px] ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: cardsVisible ? `${i * 120}ms` : "0ms"
        }}>
              {}
              <div className="relative h-64 overflow-hidden md:h-[60%]">
                <img src={lang.image} data-wvc-srcset={lang.srcset} data-wvc-sizes={lang.sizes} alt={lang.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
              </div>

              {}
              <div className="relative flex flex-1 flex-col justify-between bg-card p-7 md:p-8">
                <div>
                  {}
                  <span className="inline-flex items-center gap-2 rounded-md bg-muted px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-foreground">
                    <span className="inline-block h-1.5 w-1.5 rounded-lg bg-accent" />
                    {lang.flagLabel}
                  </span>

                  {}
                  <h3 className="font-default mt-5 text-3xl font-bold italic text-primary md:text-[2rem]">
                    {lang.greeting}
                  </h3>
                  <p className="font-default mt-1 text-xl font-semibold text-foreground md:text-2xl not-italic">
                    {lang.language}
                  </p>

                  {}
                  <p className="font-default mt-3 text-sm leading-relaxed text-muted-foreground">
                    {lang.tagline}
                  </p>
                </div>

                {}
                <div className="mt-6 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-secondary opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-lg bg-secondary" />
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {lang.learners} especificaciones
                    </span>
                  </div>

                  <span className="font-default inline-flex items-center gap-1.5 text-sm font-bold text-primary">
                    <span className="relative">
                      Ver catálogo
                      <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-accent transition-all duration-500 ease-out group-hover:w-full" />
                    </span>
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>

                {}
                <span className="pointer-events-none absolute bottom-0 left-0 h-[2px] w-0 bg-primary transition-all duration-500 ease-out group-hover:w-full" />
              </div>
            </Link>)}
        </div>
      </div>
    </section>;
}